import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Link,
  Typography,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  CloudUpload as CloudUploadIcon,
  Download as DownloadIcon,
  Handyman as HandymanIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import PrimaryActionButton from '../common/PrimaryActionButton';
import ConfirmDialog from '../common/ConfirmDialog';
import FixFailedImportServicesDialog from './FixFailedImportServicesDialog';
import {
  buildFailingServicesCsv,
  buildImportServicesPayload,
  countUniqueCategories,
  parseFreshaServiceCsv,
} from '../../utils/freshaServiceCsv';
import StoreDataImportService from '../../services/storeDataImportService';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const FRESHA_SERVICES_EXPORT_URL = 'https://partners.fresha.com/setup/services';

const uploadButtonSx = {
  borderColor: BRAND.accent,
  color: BRAND.accentText,
  textTransform: 'none',
  fontWeight: 600,
  height: 40,
  px: 2,
  '&:hover': { borderColor: BRAND.accentDark, backgroundColor: brandAlpha('accent', 0.08) },
  '&.Mui-disabled': {
    borderColor: 'rgba(0, 0, 0, 0.12)',
    color: 'rgba(0, 0, 0, 0.26)',
  },
};

const textActionSx = {
  color: BRAND.accentText,
  textTransform: 'none',
  fontWeight: 600,
  px: 0.5,
  minWidth: 0,
  '&:hover': {
    backgroundColor: 'transparent',
    color: BRAND.accentDark,
    textDecoration: 'underline',
  },
  '&.Mui-disabled': {
    color: 'rgba(0, 0, 0, 0.26)',
  },
};

const downloadTextFile = (content, fileName, mimeType = 'text/csv;charset=utf-8') => {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
};

const ImportServicesStep = ({
  storeId,
  onCompleted,
  disabled = false,
}) => {
  const { t } = useTranslation();
  const [rows, setRows] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [delimiter, setDelimiter] = useState(';');
  const [fileName, setFileName] = useState('');
  const [parseErrorKey, setParseErrorKey] = useState(null);
  const [fixOpen, setFixOpen] = useState(false);
  const [importReadyConfirmOpen, setImportReadyConfirmOpen] = useState(false);
  const [importing, setImporting] = useState(false);
  const [importError, setImportError] = useState(null);
  const [importResult, setImportResult] = useState(null);

  const failedRows = useMemo(() => rows.filter((row) => row.errors.length > 0), [rows]);
  const readyRows = useMemo(() => rows.filter((row) => row.errors.length === 0), [rows]);
  const warningCount = useMemo(
    () => rows.reduce((total, row) => total + (row.warnings?.length || 0), 0),
    [rows],
  );
  const categoryCount = useMemo(() => countUniqueCategories(readyRows), [readyRows]);

  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setImportResult(null);
    setImportError(null);

    try {
      const text = await file.text();
      const parsed = parseFreshaServiceCsv(text);
      if (parsed.errorKey) {
        setRows([]);
        setHeaders([]);
        setFileName('');
        setParseErrorKey(parsed.errorKey);
        return;
      }

      setParseErrorKey(null);
      setFileName(file.name);
      setRows(parsed.rows);
      setHeaders(parsed.headers || []);
      setDelimiter(parsed.delimiter || ';');
    } catch {
      setRows([]);
      setHeaders([]);
      setFileName('');
      setParseErrorKey('dataImport.services.errors.parseFailed');
    }
  };

  const handleSaveFixedRow = (updatedRow) => {
    setRows((current) => current.map((row) => (
      row.rowNumber === updatedRow.rowNumber ? updatedRow : row
    )));
  };

  const runImport = async (rowsToImport) => {
    setImporting(true);
    setImportError(null);
    try {
      const payload = buildImportServicesPayload(rowsToImport);
      const result = await StoreDataImportService.importServices(storeId, payload);
      setImportResult(result);
      onCompleted?.(result);
    } catch (error) {
      const message = error?.response?.data?.message
        || error?.response?.data?.title
        || t('dataImport.services.errors.importFailed');
      setImportError(message);
    } finally {
      setImporting(false);
      setImportReadyConfirmOpen(false);
    }
  };

  const handleImportClick = async () => {
    if (failedRows.length > 0 || readyRows.length === 0 || disabled) return;
    await runImport(readyRows);
  };

  const handleImportReadyConfirm = async () => {
    if (!readyRows.length || disabled) return;
    await runImport(readyRows);
  };

  const handleDownloadFailingCsv = () => {
    if (!failedRows.length) return;

    const csv = buildFailingServicesCsv(failedRows, headers, delimiter);
    const baseName = (fileName || 'fresha-services').replace(/\.csv$/i, '');
    downloadTextFile(csv, `${baseName}-failing-rows.csv`);
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      <Box
        sx={{
          p: 2.5,
          borderRadius: 2,
          border: `1px solid ${brandAlpha('accent', 0.35)}`,
          background: BRAND.background,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 700, color: BRAND.primaryText, mb: 1 }}>
          {t('dataImport.services.helper.title')}
        </Typography>
        <Typography variant="body2" sx={{ mb: 1.5 }}>
          {t('dataImport.services.helper.intro')}
        </Typography>
        <Box component="ol" sx={{ m: 0, pl: 2.5 }}>
          <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
            {t('dataImport.services.helper.step1')}
            {' '}
            <Link href={FRESHA_SERVICES_EXPORT_URL} target="_blank" rel="noopener noreferrer">
              {t('dataImport.services.helper.openFresha')}
              <OpenInNewIcon sx={{ fontSize: 14, ml: 0.5, verticalAlign: 'middle' }} />
            </Link>
          </Typography>
          <Typography component="li" variant="body2" sx={{ mb: 0.75 }}>
            {t('dataImport.services.helper.step2')}
          </Typography>
          <Typography component="li" variant="body2">
            {t('dataImport.services.helper.step3')}
          </Typography>
        </Box>
      </Box>

      <Box>
        <Button
          component="label"
          variant="outlined"
          startIcon={<CloudUploadIcon />}
          disabled={disabled || importing || Boolean(importResult)}
          sx={uploadButtonSx}
        >
          {t('dataImport.services.uploadButton')}
          <input hidden type="file" accept=".csv,text/csv" onChange={handleFileChange} />
        </Button>
        {fileName && (
          <Typography variant="body2" sx={{ mt: 1 }}>
            {t('dataImport.services.selectedFile', { fileName })}
          </Typography>
        )}
      </Box>

      {parseErrorKey && <Alert severity="error">{t(parseErrorKey)}</Alert>}
      {importError && <Alert severity="error">{importError}</Alert>}

      {rows.length > 0 && !importResult && (
        <>
          <Alert severity={failedRows.length ? 'warning' : 'success'}>
            {t('dataImport.services.summary', {
              total: rows.length,
              ready: readyRows.length,
              failed: failedRows.length,
              warnings: warningCount,
              categories: categoryCount,
            })}
          </Alert>

          {failedRows.length > 0 && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                {t('dataImport.services.failedTitle')}
              </Typography>
              {failedRows.slice(0, 8).map((row) => (
                <Typography key={row.rowNumber} variant="body2">
                  #{row.rowNumber}: {row.errors.map((errorKey) => t(errorKey)).join(', ')}
                </Typography>
              ))}
              {failedRows.length > 8 && (
                <Typography variant="body2" color="text.secondary">
                  {t('dataImport.services.moreFailures', { count: failedRows.length - 8 })}
                </Typography>
              )}

              <Box
                sx={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 2,
                  mt: 0.5,
                }}
              >
                <Button
                  variant="text"
                  startIcon={<HandymanIcon />}
                  onClick={() => setFixOpen(true)}
                  sx={textActionSx}
                >
                  {t('dataImport.services.fixFailingRows')}
                </Button>
                <Button
                  variant="text"
                  startIcon={<DownloadIcon />}
                  onClick={handleDownloadFailingCsv}
                  sx={textActionSx}
                >
                  {t('dataImport.services.downloadFailingRows')}
                </Button>
                <PrimaryActionButton
                  startIcon={<CheckCircleIcon />}
                  onClick={() => setImportReadyConfirmOpen(true)}
                  disabled={!readyRows.length || importing}
                >
                  {t('dataImport.services.importReadyRows')}
                </PrimaryActionButton>
              </Box>
            </Box>
          )}

          {warningCount > 0 && (
            <Alert severity="info">
              {t('dataImport.services.warningsNotice', { count: warningCount })}
            </Alert>
          )}

          {failedRows.length === 0 && (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <PrimaryActionButton
                onClick={handleImportClick}
                disabled={disabled || importing || readyRows.length === 0}
              >
                {importing ? t('dataImport.services.importing') : t('dataImport.services.importButton')}
              </PrimaryActionButton>
            </Box>
          )}
        </>
      )}

      {importResult && (
        <Alert severity="success">
          {t('dataImport.services.importSuccess', {
            created: importResult.createdCount,
            updated: importResult.updatedCount,
            categoriesCreated: importResult.categoriesCreatedCount,
            categoriesUpdated: importResult.categoriesUpdatedCount,
          })}
        </Alert>
      )}

      <FixFailedImportServicesDialog
        open={fixOpen}
        onClose={() => setFixOpen(false)}
        rows={rows}
        onApply={handleSaveFixedRow}
      />

      <ConfirmDialog
        open={importReadyConfirmOpen}
        onClose={() => setImportReadyConfirmOpen(false)}
        onConfirm={handleImportReadyConfirm}
        title={t('dataImport.services.importReadyConfirmTitle')}
        message={t('dataImport.services.importReadyConfirmMessage', {
          failed: failedRows.length,
          ready: readyRows.length,
        })}
        confirmText={t('dataImport.services.importReadyConfirmAction')}
        confirmColor="primary"
        loading={importing}
      />
    </Box>
  );
};

export default ImportServicesStep;
